/**
 * Voice search integration - Web Speech API
 * Uses event delegation so voice buttons work even when added dynamically (e.g. global search shortcode).
 *
 * @since 2.0.0
 */

(function () {
	'use strict';

	const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

	if (!SpeechRecognition) {
		return;
	}

	// Per-button recognition state (keyed by button element for cleanup)
	const buttonState = new WeakMap();

	function getLang() {
		const htmlLang = document.documentElement.lang || 'en-GB';
		return htmlLang.startsWith('en') ? 'en-GB' : htmlLang;
	}

	function getInputForButton(btn) {
		const inputId = btn.getAttribute('data-voice-search-target');
		if (inputId) {
			return document.getElementById(inputId);
		}
		const form = btn.closest('form');
		return form ? form.querySelector('input[type="search"], input[name="s"], input[name="search"]') : null;
	}

	function initVoiceSearch() {
		// Use event delegation so buttons work when added after DOMContentLoaded (e.g. global search widget)
		document.addEventListener('click', (e) => {
			const btn = e.target.closest('.js-voice-search-btn');
			if (!btn) return;

			e.preventDefault();
			e.stopPropagation();

			const input = getInputForButton(btn);
			if (!input) return;

			let state = buttonState.get(btn);
			if (!state) {
				state = { recognition: null, isListening: false };
				buttonState.set(btn, state);
			}

			if (state.isListening) {
				if (state.recognition) state.recognition.stop();
				return;
			}

			try {
				const recognition = new SpeechRecognition();
				recognition.continuous = false;
				recognition.interimResults = false;
				recognition.lang = getLang();
				recognition.maxAlternatives = 1;

				recognition.onstart = () => {
					state.isListening = true;
					btn.classList.add('voice-search-active');
					btn.setAttribute('aria-pressed', 'true');
					btn.setAttribute('aria-label', btn.dataset.voiceSearchListening || 'Listening…');
				};

				recognition.onresult = (event) => {
					const transcript = event.results[0][0].transcript;
					input.value = transcript;
					input.dispatchEvent(new Event('input', { bubbles: true }));
				};

				recognition.onend = () => {
					state.isListening = false;
					state.recognition = null;
					btn.classList.remove('voice-search-active');
					btn.setAttribute('aria-pressed', 'false');
					btn.setAttribute('aria-label', btn.dataset.voiceSearchLabel || 'Search with your voice');
				};

				recognition.onerror = (event) => {
					state.isListening = false;
					state.recognition = null;
					btn.classList.remove('voice-search-active');
					btn.setAttribute('aria-pressed', 'false');
					btn.setAttribute('aria-label', btn.dataset.voiceSearchLabel || 'Search with your voice');
					if (event.error === 'not-allowed') {
						input.setAttribute('title', 'Microphone access was denied.');
					}
				};

				state.recognition = recognition;
				recognition.start();
			} catch (err) {
				// Ignore if already started or unsupported
			}
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', initVoiceSearch);
	} else {
		initVoiceSearch();
	}
})();
